'use client'

import React, { useMemo, useRef, useState } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@kiao-voice/ui-kit'
import type { BlogCategory, PublicBlogPostListItem } from '@kiao-voice/api-contract/blog'
import { blogCategoryLabel } from './format'
import { PostRow } from './post-row'

/**
 * A category key, or the sentinel `'all'` — a filter state, not a category, and
 * never a value a post carries. Plain `string` because the vocabulary is data:
 * there is no union left to name.
 */
export type BlogFilterCategory = string

/** Rows shown before「載入更多」, matching the reference layout. */
const INITIAL_ROWS = 4

/**
 * Does this post belong in the current view?
 *
 * The category test is containment, NOT equality: a post carries up to two
 * categories, so `post.categories[0] === category` would make every second
 * category of every two-category post unreachable from the chip row. (The wire
 * contract hands us the array the design prototype had to encode as a
 * space-separated `data-cat`; containment here is that prototype's token split.)
 *
 * The term test is title + dek, which is everything the row itself shows — a
 * search that matched invisible text would look like it returned the wrong rows.
 */
export function matchesBlogFilter(
  post: PublicBlogPostListItem,
  category: BlogFilterCategory,
  term: string
): boolean {
  if (category !== 'all' && !post.categories.includes(category)) return false
  if (term === '') return true
  return `${post.title}\n${post.dek}`.toLowerCase().includes(term)
}

/**
 * What the list currently shows: how many articles match, and which of them are
 * on screen. One pass, because the count and the rows are the same question.
 *
 * Paging applies ONLY to the unfiltered view: once a reader has narrowed the
 * list they have asked a question, and hiding part of the answer behind a second
 * click answers it wrong.
 */
export function blogIndexView(
  posts: PublicBlogPostListItem[],
  category: BlogFilterCategory,
  term: string,
  expanded: boolean
): { matchedCount: number; visible: Set<string> } {
  const matched = posts.filter((post) => matchesBlogFilter(post, category, term))
  const filtering = category !== 'all' || term !== ''
  const limit = filtering || expanded ? matched.length : INITIAL_ROWS
  return {
    matchedCount: matched.length,
    visible: new Set(matched.slice(0, limit).map((post) => post.slug)),
  }
}

interface BlogIndexProps {
  posts: PublicBlogPostListItem[]
  /**
   * The chip row, straight from the index payload — only the categories a
   * published post carries, in the vocabulary's display order.
   */
  categories: BlogCategory[]
  /** From `?cat=` so a chip link from an article page lands pre-filtered. */
  initialCategory: BlogFilterCategory
}

/**
 * Search, category chips and the article list.
 *
 * Filtering runs in the browser over the whole published set: at this volume it
 * is instant and composable, and it costs no round trip per keystroke. Every
 * article is in the first response as a real link — 「載入更多」only unhides
 * rows — so a crawler sees the same list a reader eventually does.
 */
export function BlogIndex({ posts, categories, initialCategory }: BlogIndexProps) {
  const { t } = useTranslation('web')
  const [category, setCategory] = useState<BlogFilterCategory>(initialCategory)
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  const term = query.trim().toLowerCase()
  const filtering = category !== 'all' || term !== ''
  const { matchedCount, visible } = useMemo(
    () => blogIndexView(posts, category, term, expanded),
    [posts, category, term, expanded]
  )

  const showsLoadMore = !filtering && !expanded && matchedCount > INITIAL_ROWS
  const showsAllShownNote = !filtering && expanded && matchedCount > INITIAL_ROWS

  // Both clear actions remove the very control that was clicked, so focus would
  // otherwise fall to <body> and a keyboard reader would lose their place.
  function clearQuery() {
    setQuery('')
    searchRef.current?.focus()
  }

  function reset() {
    setCategory('all')
    clearQuery()
  }

  return (
    <>
      <section className="pb-[clamp(16px,2vw,22px)]">
        <div className="container">
          <form
            role="search"
            className="flex h-[52px] w-full items-center gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg)] px-[18px] transition-colors focus-within:border-[var(--fg)]"
            onSubmit={(event) => event.preventDefault()}
          >
            <Search aria-hidden="true" className="size-[19px] shrink-0 text-[var(--muted)]" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('blog.searchPlaceholder')}
              aria-label={t('blog.searchPlaceholder')}
              autoComplete="off"
              className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-[length:var(--text-lg)] leading-[1.25] text-[var(--fg)] outline-none placeholder:text-[var(--muted)] [&::-webkit-search-cancel-button]:hidden"
            />
            {term !== '' && (
              <button
                type="button"
                onClick={clearQuery}
                className="border-b border-transparent px-0.5 py-1 text-[length:var(--text-sm)] text-[var(--muted)] hover:border-[var(--fg)] hover:text-[var(--fg)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fg)]"
              >
                {t('blog.searchClear')}
              </button>
            )}
          </form>
        </div>
      </section>

      <section className="pb-[clamp(20px,2.4vw,26px)]">
        <div className="container">
          <div
            role="group"
            aria-label={t('blog.categoriesLabel')}
            className="-mx-2.5 flex items-center gap-1.5 overflow-x-auto px-2.5 pt-1 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <CategoryChip
              label={t('blog.categoryAll')}
              selected={category === 'all'}
              onSelect={() => setCategory('all')}
            />
            {categories.map((entry) => (
              <CategoryChip
                key={entry.key}
                label={entry.label}
                selected={category === entry.key}
                onSelect={() => setCategory(entry.key)}
              />
            ))}
          </div>
        </div>
      </section>

      <section
        id="latest"
        className="border-t border-[var(--line-soft)] py-[clamp(26px,3.4vw,38px)]"
      >
        <div className="container">
          <div className="mb-1 flex items-baseline justify-between gap-[var(--gap-md)]">
            <h2 className="text-[length:var(--fs-section-h2)]">
              {category === 'all' ? (
                <span>{t('blog.latestTitle')}</span>
              ) : (
                <span>{blogCategoryLabel(categories, category)}</span>
              )}
              {matchedCount > 0 && (
                <span className="num ml-3 text-[length:var(--text-sm)] text-[var(--muted)]">
                  {t('blog.postCount', { total: matchedCount })}
                </span>
              )}
            </h2>
          </div>

          <div>
            {posts.map((post) => (
              <PostRow
                key={post.slug}
                post={post}
                categories={categories}
                hidden={!visible.has(post.slug)}
              />
            ))}
          </div>

          {matchedCount === 0 && (
            <div className="px-0 pt-12 pb-2 text-center">
              <p className="text-[length:var(--text-lg)]">{t('blog.emptyTitle')}</p>
              <p className="mt-1.5 mb-[var(--gap-md)] text-[length:var(--text-base)] text-[var(--muted)]">
                {t('blog.emptyHint')}
              </p>
              <Button type="button" variant="secondary" onClick={reset}>
                {t('blog.emptyReset')}
              </Button>
            </div>
          )}

          {showsLoadMore && (
            // Red OUTLINE, not a red fill: §2.3 keeps the one solid red focus on
            // the selected category brush.
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="mt-[22px] flex w-full items-center justify-center gap-2.5 rounded-[var(--radius)] border border-[var(--brand)] bg-transparent px-5 py-[15px] text-[length:var(--text-lg)] font-medium text-[var(--brand)] transition-colors hover:border-[var(--brand-hover)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand-hover)] focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[var(--fg)]"
            >
              {t('blog.loadMore')}
              <ChevronDown aria-hidden="true" className="size-[17px]" />
            </button>
          )}
          {showsAllShownNote && (
            <p className="meta mt-[22px] text-center text-[length:var(--text-xs-plus)]">
              {t('blog.allShown', { total: matchedCount })}
            </p>
          )}
        </div>
      </section>
    </>
  )
}

interface CategoryChipProps {
  label: string
  selected: boolean
  onSelect: () => void
}

/**
 * The selected chip is the page's one solid red focus (§2.3): an ink brush
 * carrying paper-white type.
 *
 * The asset holds only ink and transparency — the red comes from
 * `background-color: var(--brand)` showing through the mask — so the state
 * colour stays a UI decision instead of being baked into a picture
 * (guideline §5.3 / §5.4). Stretching one brush across labels of different
 * widths deforms it slightly, which is what a real brush stroke does.
 */
function CategoryChip({ label, selected, onSelect }: CategoryChipProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={`relative shrink-0 rounded-[var(--radius-sm)] border-0 bg-transparent px-5 py-2.5 text-[length:var(--text-lg)] font-medium whitespace-nowrap transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fg)] ${
        selected ? 'text-[var(--bg)]' : 'text-[var(--fg)] hover:bg-[var(--fg-soft)]'
      }`}
    >
      {selected && (
        <span
          aria-hidden="true"
          className="absolute -top-2 -left-3 h-[calc(100%+11px)] w-[calc(100%+24px)] bg-[var(--brand)]"
          style={{
            maskImage: "url('/blog/chip-brush.webp')",
            maskSize: '100% 100%',
            maskRepeat: 'no-repeat',
            WebkitMaskImage: "url('/blog/chip-brush.webp')",
            WebkitMaskSize: '100% 100%',
            WebkitMaskRepeat: 'no-repeat',
          }}
        />
      )}
      <span className="relative z-1">{label}</span>
    </button>
  )
}
